import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';

import { formatPrice } from 'utilities/money';

import { GreenButton } from '@marketing/components/buttons';
import SectionHeader from '@marketing/components/section_header';

import CheckmarkIcon from './assets/checkmark_icon.svg';
import CircleChevronIcon from './assets/circle_chevron_icon.svg';
import MagicWandIcon from './assets/magic_wand_icon.svg';
import MagooshLogo from '@magoosh/assets/magoosh-logo-purple.png';
import styles from './style.module.scss';

interface Props {
  companyFeatureOfferings: {
    name: string;
    [features: string]: any;
  }[];
  features: {
    icon: string;
    name: string;
  }[];
  registrationUrl: string;
}

const CompetitorComparisonSection: React.FC<Props> = (props) => {
  const tableRef = useRef(null);
  const magooshColumnRef = useRef(null);
  const [currentCompanyIndex, setCurrentCompanyIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [gradientBgDimensions, setGradientBgDimensions] = useState(null);

  const determineGradientBgDimensions = useRef(() => {
    setGradientBgDimensions({
      height: tableRef.current.getBoundingClientRect().height,
      left: magooshColumnRef.current.getBoundingClientRect().left - tableRef.current.getBoundingClientRect().left - 10,
      top: 0,
      width: magooshColumnRef.current.getBoundingClientRect().width
    });
  });

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);

    window.addEventListener('resize', () => {
      if (window.innerWidth < 768 && !isMobile) {
        setIsMobile(true);
      } else if (window.innerWidth > 768 && isMobile) {
        setIsMobile(false);
      }
    });
  });

  useLayoutEffect(() => {
    determineGradientBgDimensions.current();
    window.addEventListener('resize', determineGradientBgDimensions.current);

    return () => {
      window.removeEventListener('resize', determineGradientBgDimensions.current);
    };
  }, []);

  const advanceToNextCompany = () => {
    if (currentCompanyIndex === props.companyFeatureOfferings.length - 1) return;

    setCurrentCompanyIndex(currentCompanyIndex + 1);
  };

  const currentCompanyName = () => {
    return props.companyFeatureOfferings[currentCompanyIndex].name;
  };

  const returnToPreviousCompany = () => {
    if (currentCompanyIndex === 0) return;

    setCurrentCompanyIndex(currentCompanyIndex - 1);
  };

  const Checkmark = (
    <img
      alt="Checkmark icon"
      height="16px"
      loading="lazy"
      src={CheckmarkIcon}
      style={{ height: 16, marginTop: 2, width: 14 }}
      width="14px"
    />
  );

  const NotAvailable = (
    <div className="small text-inter" style={{ color: '#B2B2B2' }}>-</div>
  );

  return (
    <div className="d-flex flex-column gap-m">
      <SectionHeader
        className="u-margin-B-m"
        subtitle="See for yourself how Magoosh stacks up with others in the field."
        supertitle="Competitive advantage"
        supertitleIcon={MagicWandIcon}
        title="How Magoosh stacks up against other options"
      />

      <div className="visible-xs visible-sm">
        <div className="align-items-center d-flex gap-s">
          <div className="flex-none">
            <img
              alt="Left arrow icon"
              onClick={returnToPreviousCompany}
              src={CircleChevronIcon}
              style={{ opacity: currentCompanyIndex === 0 ? 0.3 : 1 }}
            />
          </div>

          <div className="flex-1 text-center">
            {
              currentCompanyName() === 'Magoosh' ? (
                <div style={{ height: 28 }}>
                  <img alt="Magoosh logo" height="28px" src={MagooshLogo} width="121px" />
                </div>
              ) : (
                <div
                  className="text-bold text-inter"
                  style={{ fontSize: 18, height: 28 }}
                >
                  {currentCompanyName()}
                </div>
              )
            }
          </div>

          <div className="flex-none">
            <img
              alt="Right arrow icon"
              onClick={advanceToNextCompany}
              src={CircleChevronIcon}
              style={{
                opacity: currentCompanyIndex === props.companyFeatureOfferings.length - 1 ? 0.3 : 1,
                transform: 'scaleX(-1)'
              }}
            />
          </div>
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        {
          currentCompanyName() === 'Magoosh' && gradientBgDimensions  && (
            <div
              style={{
                background: 'linear-gradient(to bottom, transparent, #D4F0E5)',
                borderRadius: '0 0 22px 22px',
                position: 'absolute',
                ...gradientBgDimensions
              }}
            >
            </div>
          )
        }

        <table
          className={cx(styles.featureComparisonTable, `active-option-${currentCompanyIndex}`)}
          ref={tableRef}
        >
          <thead>
            <th className="hidden-xs hidden-sm">
              <div className="text-inter u-margin-B-m" style={{ fontSize: 18, fontWeight: 400, height: 28 }}>
                Features breakdown
              </div>
            </th>

            {
              props.companyFeatureOfferings.map((companyOffering) => (
                <th key={companyOffering.name} className="hidden-xs hidden-sm">
                  {
                    companyOffering.name === 'Magoosh' ? (
                      <div className="u-margin-B-m" style={{ height: 28 }}>
                        <img alt="Magoosh logo" height="28px" src={MagooshLogo} width="121px" />
                      </div>
                    ) : (
                      <div className="text-inter u-margin-B-m" style={{ fontSize: 18, height: 28 }}>
                        {companyOffering.name}
                      </div>
                    )
                  }
                </th>
              ))
            }
          </thead>

          <tbody>
            {
              props.features.map((feature, idx) => (
                <tr key={feature.name}>
                  <td>
                    <div className="d-flex gap-xs">
                      <img alt="" height="16px" loading="lazy" src={feature.icon} style={{ height: 16, marginTop: 2, width: 14 }} width="14px" />
                      <div className="small text-inter">{feature.name}</div>
                    </div>
                  </td>

                  {
                    props.companyFeatureOfferings.map((companyOffering) => (
                      <td
                        key={companyOffering.name}
                        ref={idx === 0 && companyOffering.name === 'Magoosh' ? magooshColumnRef : null}
                      >
                        {
                          companyOffering[feature.name] ? (
                            <div className="d-flex gap-xs">
                              {Checkmark}
                              <div className={cx('small text-inter', { 'text-bold': companyOffering.name === 'Magoosh' })}>
                                {companyOffering[feature.name]}
                              </div>
                            </div>
                          ) : NotAvailable
                        }
                      </td>
                    ))
                  }
                </tr>
              ))
            }

            <tr>
              <td />

              <td>
                <div className="d-flex gap-xs">
                  <GreenButton
                    block
                    href={props.registrationUrl}
                    style={{ width: 'calc(100% - 20px)' }}
                  >
                    Sign Up Now
                  </GreenButton>
                </div>
              </td>

              <td />
              <td />
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompetitorComparisonSection;
